import Joi from 'joi'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { connect } from 'react-redux'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm, useFieldArray } from 'react-hook-form'

import http from '../../../../../shared/http'

import { TApi } from '../../../../../shared/const'
import { schemaOptions } from '../../../../../shared/i18n'

import Btn from '../../../../../UI/Btn'
import BtnAdd from '../../../../../UI/BtnAdd'
import SelectField from '../../../../../UI/SelectField'

import StatusIcon from '../../../../../UI/StatusIcon'
import { optionsFor, canonicalOptionValue } from '../../../../../shared/optionList'

import styles from '../style.module.scss'

const schema = Joi.object({
	primaryWorkArea: Joi.string().required(),
	workAreas: Joi.array()
		.items(
			Joi.object({
				workArea: Joi.string().allow('')
			})
		)
		.max(2)
})

function WorkArea(props) {
	const { t, i18n } = useTranslation()
	const {
		control,
		reset,
		watch,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})
	const firstWorkAreaWatch = watch('primaryWorkArea')

	const {
		fields: areasFields,
		append,
		remove
	} = useFieldArray({
		reset,
		control,
		name: 'workAreas'
	})
	const [isLoading, setLoading] = useState(false)
	const isCEO = useMemo(
		() => (props.modalStudent ? props.modalStudent.isCEO : props.isCEO),
		[props.isCEO, props.modalStudent]
	)
	const onSubmit = data => {
		setLoading(true)

		const url = props.modalStudent
			? TApi.STUDENT_PUT_EMPLOYEES(props.modalStudent.studentId)
			: TApi.STUDENT_PUT_PROFILE
		const body = {
			primaryWorkArea: data.primaryWorkArea,
			secondaryWorkArea: data.workAreas[0] ? data.workAreas[0].workArea : '',
			tertiaryWorkArea: data.workAreas[1] ? data.workAreas[1].workArea : ''
		}

		http.put(url, body)
			.then(() => {
				toast.success(t('Profile has been updated successfully.'))

				if (props.refreshEmployees) props.refreshEmployees()
			})
			.finally(() => setLoading(false))
	}
	/*
	 * Die Liste kommt aus `shared/optionList`, nicht aus einem eigenen `map`:
	 * An älteren Datensätzen steht der Arbeitsbereich als **deutscher** Text
	 * ("Verkauf"), die Felder verglichen aber gegen `value_EN` ("Sales") — ohne
	 * Treffer zeigte react-select seinen Platzhalter, obwohl ein Wert gespeichert
	 * war. `optionsFor` hängt einen unbekannten Wert zusätzlich an, damit er nicht
	 * still verschwindet; `canonicalOptionValue` (unten) setzt den gespeicherten
	 * Wert auf den Schlüssel der Liste um.
	 */
	const workAreaOptions = ({ isInjectCEO, storedValue } = { isInjectCEO: false }) =>
		optionsFor(props.workAreas, i18n.language, canonicalOptionValue(props.workAreas, storedValue)).filter(
			v => isInjectCEO || !(/ceo/i.test(v.value || '') || /ceo/i.test(v.label || ''))
		)

	useEffect(() => {
		let areas = []

		const primaryWorkArea = props.modalStudent ? props.modalStudent.primaryWorkArea : props.primaryWorkArea
		const secondaryWorkArea = props.modalStudent ? props.modalStudent.secondaryWorkArea : props.secondaryWorkArea
		const tertiaryWorkArea = props.modalStudent ? props.modalStudent.tertiaryWorkArea : props.tertiaryWorkArea

		const ceo = props.workAreas?.find(v => /ceo/i.test(v.value_EN || '') || /ceo/i.test(v.value_DE || ''))

		if (secondaryWorkArea) areas.push({ workArea: canonicalOptionValue(props.workAreas, secondaryWorkArea) })
		if (tertiaryWorkArea) areas.push({ workArea: canonicalOptionValue(props.workAreas, tertiaryWorkArea) })

		/*
		 * `reset` statt `setValue`: Die Auswahlfelder hängen an einem
		 * `Controller`. Wird das Formular erst befüllt, nachdem die Felder schon
		 * stehen — so ist es im Fenster einer Mitarbeiter:in, das mit den Daten
		 * in der Hand aufgeht —, kam der Wert per `setValue` nicht mehr an. `reset`
		 * setzt den ganzen Stand samt der Zusatzbereiche und greift unabhängig
		 * davon, wann die Felder angemeldet wurden.
		 */
		reset({
			primaryWorkArea: isCEO
				? ceo
					? ceo.value_EN
					: canonicalOptionValue(props.workAreas, primaryWorkArea)
				: canonicalOptionValue(props.workAreas, primaryWorkArea),
			workAreas: areas
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.modalStudent, props.primaryWorkArea, props.secondaryWorkArea, props.tertiaryWorkArea, isCEO, props.workAreas])

	return (
		<div className={styles.root}>
			{/*
			  * `formId`: Wer das Formular einbettet, kann seinen eigenen
			  * Absenden-Knopf woanders hinstellen (Fenster einer Mitarbeiter:in:
			  * "Speichern" steht dort mit "Loeschen" in einer Fussleiste) und
			  * setzt dann `hideSubmit`.
			  */}
			<form id={props.formId} onSubmit={handleSubmit(onSubmit)}>
				<div className={clsx(styles.section, props.modalStudent && styles.sectionModal)}>
					<h1 className={clsx(styles.sectionTitle, props.modalStudent && styles.sectionTitleModal)}>
						{t('Functional and working area')}
						{!isCEO && !firstWorkAreaWatch && (
							<StatusIcon
								name="warning"
								size={18}
								className={styles.warningParentalConfirmation}
								title={t('Edit function & working area')}
							/>
						)}
					</h1>
					<div className={styles.sectionContent}>
						<div className={styles.mainFunction}>
							<SelectField
								required
								disabled={isLoading || isCEO}
								mb={20}
								label={t('Main function')}
								control={control}
								name="primaryWorkArea"
								error={errors.primaryWorkArea}
								placeholder={t('Your main work area')}
								options={workAreaOptions({ isInjectCEO: isCEO, storedValue: firstWorkAreaWatch })}
							/>
							{isCEO && (
								<svg
									className={styles.info}
									data-class={styles.infoTooltip}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									data-tip={t(
										'The CEO role must always be assigned to one person. It can be assigned to another person via the 3-point menu in the "Employees" view. CEO name is shown on the company register extract.'
									)}>
									<circle cx="12" cy="12" r="10" />
									<line x1="12" y1="16" x2="12" y2="12" />
									<line x1="12" y1="8" x2="12.01" y2="8" />
								</svg>
							)}
						</div>

						{areasFields.map((item, index) => (
							<div key={item.id} className={styles.flexContainer}>
								<SelectField
									disabled={isLoading}
									mb={20}
									label={t('Work area')}
									control={control}
									name={`workAreas.${index}.workArea`}
									error={errors.workAreas && errors.workAreas[index]?.workArea}
									placeholder={t('Your work area')}
									options={workAreaOptions({ storedValue: watch(`workAreas.${index}.workArea`) })}
								/>

								<Btn
									disabled={isLoading}
									theme="white"
									icon="delete"
									iconColor="#000"
									onClick={() => remove(index)}
								/>
							</div>
						))}

						{areasFields.length <= 1 && !props.isTeacherStudentView && (
							<div className={styles.btnAdd}>
								<BtnAdd disabled={isLoading} type="button" onClick={() => append({ workArea: '' })} />
								<h1 className={styles.btnTitle}>{t('Add work area')}</h1>
							</div>
						)}
						{!props.isTeacherStudentView && !props.hideSubmit && (
							<div className={styles.btnContainer}>
								<Btn
									loading={isLoading}
									className={clsx(styles.btn)}
									type="submit"
									icon="arrow"
									iconSize={{ w: 10, h: 10 }}
									iconColor="#fff">
									{t('save')}
								</Btn>
							</div>
						)}
					</div>
				</div>
			</form>
		</div>
	)
}

WorkArea.propTypes = {
	primaryWorkArea: PropTypes.string.isRequired,
	secondaryWorkArea: PropTypes.string.isRequired,
	tertiaryWorkArea: PropTypes.string.isRequired,
	workAreas: PropTypes.array,
	isCEO: PropTypes.bool.isRequired,
	modalStudent: PropTypes.shape({
		studentId: PropTypes.number.isRequired,
		isCEO: PropTypes.bool.isRequired,
		primaryWorkArea: PropTypes.string.isRequired,
		secondaryWorkArea: PropTypes.string.isRequired,
		tertiaryWorkArea: PropTypes.string.isRequired
	}),
	refreshEmployees: PropTypes.func,
	isTeacherStudentView: PropTypes.bool.isRequired,
	/** Kennung des Formulars, damit ein Knopf ausserhalb es absenden kann. */
	formId: PropTypes.string,
	/** Eigenen Speichern-Knopf weglassen — der Rahmen stellt ihn selbst. */
	hideSubmit: PropTypes.bool
}

WorkArea.defaultProps = {
	modalStudent: null,
	refreshEmployees: null
}
const mapStateToProps = state => ({
	primaryWorkArea: state.user.profile.primaryWorkArea,
	secondaryWorkArea: state.user.profile.secondaryWorkArea,
	tertiaryWorkArea: state.user.profile.tertiaryWorkArea,
	isCEO: state.user.profile.isCEO,
	workAreas: state.options.workAreas
})

export default connect(mapStateToProps)(WorkArea)
